import { useCallback, useRef, useState } from 'react';
import { Upload as UploadIcon } from 'lucide-react';
import { ingestFinalize, ingestInit, uploadToR2 } from '../api/ingest';

type Phase = 'idle' | 'initializing' | 'uploading' | 'finalizing' | 'queued' | 'failed';

interface State {
  phase: Phase;
  progress: number;
  filename?: string;
  mediaId?: string;
  error?: string;
}

export function UploadDropZone({ onQueued }: { onQueued?: (mediaId: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ phase: 'idle', progress: 0 });
  const [dragOver, setDragOver] = useState(false);

  const startUpload = useCallback(async (file: File) => {
    setState({ phase: 'initializing', progress: 0, filename: file.name });
    try {
      const init = await ingestInit({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      setState({ phase: 'uploading', progress: 0, filename: file.name, mediaId: init.mediaId });

      await uploadToR2(init.uploadUrl, file, (frac) => {
        setState((s) => ({ ...s, phase: 'uploading', progress: Math.round(frac * 100) }));
      });

      setState((s) => ({ ...s, phase: 'finalizing', progress: 100 }));
      const fin = await ingestFinalize(init.mediaId);
      setState({ phase: 'queued', progress: 100, filename: file.name, mediaId: init.mediaId });
      onQueued?.(fin.mediaId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, phase: 'failed', error: msg }));
    }
  }, [onQueued]);

  const onSelect = (file: File | undefined | null) => {
    if (!file) return;
    if (state.phase !== 'idle' && state.phase !== 'queued' && state.phase !== 'failed') return;
    void startUpload(file);
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onSelect(e.dataTransfer.files?.[0]);
        }}
        className={[
          'flex w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed bg-surface px-8 py-16 transition-colors',
          dragOver ? 'border-primary bg-surface-2' : 'border-surface-divider hover:border-primary',
        ].join(' ')}
      >
        <UploadIcon size={36} className="text-primary" />
        <div className="text-base font-medium">Drop a movie here, or click to choose</div>
        <div className="text-sm text-text-secondary">MP4, MKV, MOV, WebM. Up to ~20 GB.</div>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => onSelect(e.target.files?.[0])}
        />
      </button>

      {state.phase !== 'idle' && (
        <div className="rounded-2xl border border-surface-divider bg-surface p-4">
          <div className="flex items-center justify-between">
            <div className="truncate text-sm font-medium">{state.filename}</div>
            <div className="text-xs text-text-secondary">{labelFor(state.phase)}</div>
          </div>
          {(state.phase === 'uploading' || state.phase === 'finalizing' || state.phase === 'queued') && (
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-base-300">
              <div className="h-full bg-primary transition-all" style={{ width: `${state.progress}%` }} />
            </div>
          )}
          {state.error && (
            <div className="mt-2 text-sm text-red-600">{state.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

function labelFor(p: Phase): string {
  switch (p) {
    case 'initializing': return 'Preparing upload…';
    case 'uploading': return 'Uploading…';
    case 'finalizing': return 'Queueing transcode…';
    case 'queued': return 'Transcoding queued';
    case 'failed': return 'Failed';
    default: return '';
  }
}
